import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import { blogAPI } from 'utils/api';

// Turn a blog category name into its CSS color class.
function blogCatClass(name) {
  if (!name) return 'blog-cat-default';
  const slug = name.toLowerCase().replace(/&/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const known = ['technology','web-development','python','cyber-security','ai-ml','general'];
  return known.includes(slug) ? `blog-cat-${slug}` : 'blog-cat-default';
}


const CATS = [
  { icon: 'pdf',   title: 'PDF Tools',     desc: '7 tools — merge, split, compress, convert',  cat:'pdf',     path: '/tools?cat=pdf' },
  { icon: 'image', title: 'Image Tools',   desc: '5 tools — resize, compress, convert, remove BG', cat:'image',   path: '/tools?cat=image' },
  { icon: 'type',  title: 'Text Tools',    desc: 'Word counter, case converter',               cat:'text',    path: '/tools?cat=text' },
  { icon: 'tools', title: 'Utility Tools', desc: 'QR code, resume builder and more',                 cat:'utility', path: '/tools?cat=utility' },
];

const POPULAR = [
  { icon:'merge',   name:'Merge PDF',           desc:'Combine multiple PDFs into one',      cat:'pdf',   badge:'PDF',   path:'/tools/pdf/merge' },
  { icon:'scissors',name:'Split PDF',            desc:'Extract specific pages from PDF',     cat:'pdf',   badge:'PDF',   path:'/tools/pdf/split' },
  { icon:'compress',name:'PDF Compressor',       desc:'Reduce PDF file size quickly',        cat:'pdf',   badge:'PDF',   path:'/tools/pdf/compress' },
  { icon:'image',   name:'Image to PDF',         desc:'Convert any image to PDF',            cat:'pdf',   badge:'PDF',   path:'/tools/pdf/image-to-pdf' },

  { icon:'resize',  name:'Image Resize',         desc:'Resize to exact dimensions',          cat:'image', badge:'Image', path:'/tools/image/resize' },
  { icon:'compress',name:'Image Compressor',     desc:'Compress without quality loss',       cat:'image', badge:'Image', path:'/tools/image/compress' },
  { icon:'word',    name:'PDF to Word',          desc:'Extract text as editable document',   cat:'pdf',   badge:'PDF',   path:'/tools/pdf/to-word' },
];

const FEATURES = [
  { icon:'zap',    title:'Lightning Fast',  desc:'Files processed in seconds on our server.' },
  { icon:'shield', title:'100% Free',       desc:'No signup. No watermarks. No limits.' },
  { icon:'lock',   title:'Private & Secure', desc:'Files deleted automatically after processing.' },
];

export default function HomePage() {
  const [search, setSearch] = useState('');
  const [blogs, setBlogs]   = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    blogAPI.getLatest().then(r => setBlogs(r.data)).catch(() => {});
  }, []);

  const handleSearch = e => {
    e.preventDefault();
    if (search.trim()) nav(`/tools?search=${encodeURIComponent(search)}`);
  };

  return (
    <>
    <Seo path="/" />
    <div>
      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-badge"><Icon name="zap" size={14} />Free Online Tools — No Signup Required</div>
          <h1>Free PDF &amp; Image<br /><span className="accent">Tools for Everyone</span></h1>
          <p>Merge, split, compress PDFs. Resize, compress, remove backgrounds from images. All free. All fast.</p>
          <form className="search-wrap" onSubmit={handleSearch}>
            <div className="search-icon"><Icon name="search" size={18} /></div>
            <input type="text" placeholder="Search tools — e.g. merge PDF, remove background..." value={search} onChange={e => setSearch(e.target.value)} />
            <button type="submit"><Icon name="search" size={15} />Search</button>
          </form>
          <Link to="/tools" className="hero-tools-btn"><Icon name="tools" size={17} />Browse All Tools</Link>
        </div>
      </section>

      {/* Features bar */}
      <div style={{ background:'var(--white)', borderBottom:'1px solid var(--slate-100)', padding:'24px 0' }}>
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:20 }}>
            {FEATURES.map(f => (
              <div key={f.title} style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
                <div style={{ width:38, height:38, background:'var(--blue-50)', borderRadius:'var(--r-sm)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--blue)', flexShrink:0 }}>
                  <Icon name={f.icon} size={19} />
                </div>
                <div>
                  <p style={{ fontWeight:700, fontSize:14, color:'var(--slate-800)', marginBottom:2 }}>{f.title}</p>
                  <p style={{ fontSize:13, color:'var(--slate-400)', lineHeight:1.5 }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Featured / Quick Tools — animated demos */}
      <section className="section">
        <div className="container">
          <div className="sec-header">
            <div><h2>Most Popular Tools</h2><p>Jump straight to what people use most</p></div>
          </div>
          <div className="featured-grid">

            <Link to="/tools/utility/resume-builder" className="featured-card">
              <div className="fd-demo fd-resume">
                <span className="fd-r1"></span><span className="fd-r2"></span><span className="fd-r3"></span><span className="fd-r4"></span>
              </div>
              <div className="featured-body"><h3>Resume Builder</h3><p>Type and watch it build live</p></div>
              <span className="featured-link">Open tool <Icon name="arrowRight" size={16} /></span>
            </Link>

            <Link to="/tools/pdf/image-to-pdf" className="featured-card">
              <div className="fd-demo fd-img">
                <div className="fd-photo"><i></i></div>
                <div className="fd-arrow">&rarr;</div>
                <div className="fd-pdf"><b className="l1"></b><b className="l2"></b><b className="l3"></b></div>
              </div>
              <div className="featured-body"><h3>Image to PDF</h3><p>Photo becomes a clean PDF</p></div>
              <span className="featured-link">Open tool <Icon name="arrowRight" size={16} /></span>
            </Link>

            <Link to="/tools/utility/qr-code" className="featured-card">
              <div className="fd-demo fd-qr">
                <div className="fd-qr-box">
                  <i className="fd-fade"></i><i></i><i className="o"></i><i className="fd-fade"></i>
                  <i></i><i className="o"></i><i className="fd-fade"></i><i className="o"></i>
                  <i className="o"></i><i className="fd-fade"></i><i></i><i></i>
                  <i className="fd-fade"></i><i className="o"></i><i></i><i className="fd-fade"></i>
                  <div className="fd-scan"></div>
                </div>
              </div>
              <div className="featured-body"><h3>QR Code Generator</h3><p>Make a QR in one click</p></div>
              <span className="featured-link">Open tool <Icon name="arrowRight" size={16} /></span>
            </Link>

          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="section section-alt">
        <div className="container">
          <div className="sec-header">
            <div><h2>Browse by Category</h2><p>Choose a category to find the right tool</p></div>
          </div>
          <div className="cat-grid">
            {CATS.map(c => (
              <Link to={c.path} key={c.title} className={`cat-card cat-${c.cat}`}>
                <div className="cat-icon"><Icon name={c.icon} size={24} /></div>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Tools */}
      <section className="section">
        <div className="container">
          <div className="sec-header">
            <div><h2>Popular Tools</h2><p>Most used tools by our users</p></div>
            <Link to="/tools" className="see-all">All Tools <Icon name="arrowRight" size={16} /></Link>
          </div>
          <div className="tools-grid">
            {POPULAR.map(t => (
              <Link to={t.path} key={t.name} className="tool-card">
                <div className={`tool-icon cat-${t.cat}`}><Icon name={t.icon} size={19} /></div>
                <h3>{t.name}</h3>
                <p>{t.desc}</p>
                <span className={`tool-badge cat-${t.cat}`}>{t.badge}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Blog */}
      <section className="section section-alt">
        <div className="container">
          <div className="sec-header">
            <div><h2>Latest Articles</h2><p>Tips, guides and tutorials</p></div>
            <Link to="/blog" className="see-all">All Articles <Icon name="arrowRight" size={16} /></Link>
          </div>
          {blogs.length === 0 ? (
            <div style={{ textAlign:'center', padding:'56px', background:'var(--slate-50)', borderRadius:'var(--r-lg)', border:'1.5px dashed var(--slate-200)' }}>
              <div style={{ width:48, height:48, background:'var(--slate-200)', borderRadius:'var(--r)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px', color:'var(--slate-400)' }}>
                <Icon name="blog" size={22} />
              </div>
              <p style={{ color:"var(--slate-400)" }}>No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="blog-grid">
              {blogs.map(b => (
                <Link to={`/blog/${b.slug}`} key={b._id} className="blog-card">
                  <div className="blog-card-body">
                    <span className={`cat-badge ${blogCatClass(b.category)}`}>{b.category}</span>
                    <h3>{b.title}</h3>
                    <p>{b.excerpt}</p>
                  </div>
                  <div className="blog-card-foot">
                    <span style={{ display:'flex', alignItems:'center', gap:5 }}><Icon name="calendar" size={12} />{new Date(b.createdAt).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</span>
                    <span className="read-more">Read <Icon name="arrowRight" size={13} /></span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section section-alt">
        <div className="container" style={{ textAlign:'center' }}>
          <h2 style={{ fontSize:28, marginBottom:12, letterSpacing:'-.5px' }}>Start using free tools today</h2>
          <p style={{ color:'var(--slate-400)', marginBottom:28, fontSize:16 }}>No account needed. No watermarks. Completely free.</p>
          <Link to="/tools" className="btn btn-primary btn-lg">Explore All Tools <Icon name="arrowRight" size={18} /></Link>
        </div>
      </section>
    </div>
  </>
  );
}